import React, { useEffect, useRef, useState } from 'react';
import { X, Plus, Shield, User } from 'lucide-react';
import api from '../services/api';

/**
 * Lista de participantes (usuarios o agentes) con buscador para agregar más.
 * value: [{ email, nombre, tipo, id? }] · onAdd(persona) · onRemove(persona) · canEdit
 */
export default function ParticipantesPicker({ value = [], onAdd, onRemove, canEdit = false, titulo = 'Participantes' }) {
    const [abierto, setAbierto] = useState(false);
    const [q, setQ] = useState('');
    const [resultados, setResultados] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const caja = useRef(null);

    useEffect(() => {
        if (!abierto || q.trim().length < 2) { setResultados([]); return undefined; }
        let vivo = true;
        const t = setTimeout(async () => {
            setBuscando(true);
            try {
                const r = await api.get('/tickets/participantes/buscar', { params: { q: q.trim() } });
                if (vivo) setResultados(Array.isArray(r.data) ? r.data : []);
            } catch { if (vivo) setResultados([]); }
            finally { if (vivo) setBuscando(false); }
        }, 250);
        return () => { vivo = false; clearTimeout(t); };
    }, [q, abierto]);

    useEffect(() => {
        if (!abierto) return undefined;
        const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
        document.addEventListener('mousedown', fuera);
        return () => document.removeEventListener('mousedown', fuera);
    }, [abierto]);

    const yaEsta = (email) => value.some((p) => p.email?.toLowerCase() === email.toLowerCase());
    const elegir = (p) => { onAdd?.(p); setQ(''); setResultados([]); setAbierto(false); };

    return (
        <div ref={caja}>
            <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{titulo}</h3>
                {canEdit && (
                    <button type="button" onClick={() => setAbierto((a) => !a)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline">
                        <Plus size={13} /> Agregar
                    </button>
                )}
            </div>

            {canEdit && abierto && (
                <div className="relative mb-2">
                    <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar usuario o agente por nombre o correo…"
                        className="w-full px-3 py-2 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500" />
                    {(resultados.length > 0 || buscando || q.trim().length >= 2) && (
                        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg">
                            {buscando && <p className="px-3 py-2 text-xs text-gray-400">Buscando…</p>}
                            {!buscando && resultados.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">Sin resultados</p>}
                            {resultados.map((p) => {
                                const repetido = yaEsta(p.email);
                                return (
                                    <button key={p.email} type="button" disabled={repetido} onClick={() => elegir(p)}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40">
                                        {p.tipo === 'agente' ? <Shield size={14} className="text-blue-500 shrink-0" /> : <User size={14} className="text-gray-400 shrink-0" />}
                                        <span className="flex-1 min-w-0">
                                            <span className="block truncate text-gray-900 dark:text-white">{p.nombre}</span>
                                            <span className="block truncate text-xs text-gray-400">{p.email}{p.detalle ? ` · ${p.detalle}` : ''}</span>
                                        </span>
                                        {repetido && <span className="text-xs text-gray-400">ya agregado</span>}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {value.length === 0 ? (
                <p className="text-xs text-gray-400">{canEdit ? 'Sin participantes adicionales.' : 'Sin participantes.'}</p>
            ) : (
                <div className="flex flex-wrap gap-1.5">
                    {value.map((p) => (
                        <span key={p.email} title={p.email}
                            className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium border ${p.tipo === 'agente' ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300' : 'bg-gray-50 border-gray-200 text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200'}`}>
                            {p.tipo === 'agente' ? <Shield size={11} /> : <User size={11} />}
                            {p.nombre}
                            {canEdit && (
                                <button type="button" onClick={() => onRemove?.(p)} aria-label={`Quitar a ${p.nombre}`} className="p-0.5 rounded-full hover:bg-black/10">
                                    <X size={11} />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
